// The offboarding inspection on an owner offboarding case (db/migrations/9700,
// docs/OWNER-OFFBOARDING-INSPECTION.md). Sits on the hand back step: the hand
// back is refused until an inspection is filed with no faults, or with the
// faults accepted in writing.
//
// Built for a phone in Musa's hand beside the car. Each photo uploads the
// moment it is taken, so a weak signal costs one photo and not the whole form,
// and the answers are kept on the phone as a draft until the inspection is
// filed. The draft holds paths and words only, never the photos themselves.
//
// What counts as complete is decided in owner_offboarding_inspect_v1; the
// counter here uses the same rule (answerMissing) only to show what is left.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Camera, Check, ChevronDown, ChevronRight, ClipboardCheck, ExternalLink, X } from 'lucide-react'
import {
  useAcceptOwnerInspectionFaults, useGetOwnerInspectionChecklist, useOpenOwnerInspectionPhoto,
  useRecordOwnerOffboardingInspection, useSignOwnerInspectionPhoto,
} from '../../hooks/backend/owners'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { formatDay, formatWhen } from '../../utils/loans'
import { uploadSigned, type Signed } from '../../utils/signedUpload'
import { answerMissing, type InspectionItem, type OffboardCase, type OffboardInspection } from '../../utils/ownerOffboarding'

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

type Answer = { result?: 'pass' | 'fail' | 'na'; note?: string; photo?: string }
type Draft = { request_key: string; odometer: string; notes: string; answers: Record<string, Answer> }

const draftKey = (caseId: string) => `owner-offboarding-inspection:${caseId}`

function readDraft(caseId: string): Draft {
  try {
    const raw = localStorage.getItem(draftKey(caseId))
    if (raw) {
      const d = JSON.parse(raw) as Draft
      if (d && typeof d.request_key === 'string' && d.answers) return d
    }
  } catch { /* a private window or cleared storage starts empty */ }
  return { request_key: crypto.randomUUID(), odometer: '', notes: '', answers: {} }
}

function saveDraft(caseId: string, d: Draft) {
  try { localStorage.setItem(draftKey(caseId), JSON.stringify(d)) } catch { /* the form still works without it */ }
}

function clearDraft(caseId: string) {
  try { localStorage.removeItem(draftKey(caseId)) } catch { /* nothing to clear */ }
}

/** The inspection block on the hand back step. */
export function OffboardingInspection({ c, canAct, onChanged }: { c: OffboardCase; canAct: boolean; onChanged: () => void }) {
  const [formOpen, setFormOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const ins = c.inspection
  const handedBack = c.handed_back_on !== null
  const faults = ins?.answers.filter((a) => a.result === 'fail') ?? []

  return (
    <div className="mt-2 space-y-2 pl-7 text-xs">
      {!ins ? (
        <p className="text-muted-foreground">
          No inspection yet. Before the hand back, the car is inspected end to end: photos of every side, the roof, the ceiling, the seats and the
          engine bay, then every light, the gears, a test drive, a full tank, the spare wheel, triangle and jack.
        </p>
      ) : (
        <div className="space-y-1 rounded-md border border-border p-2">
          <div className="flex flex-wrap items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-foreground">Inspected {formatDay(ins.inspected_on)}{ins.by_name ? ` by ${ins.by_name}` : ''}</span>
            {ins.fail_count === 0
              ? <Chip tone="green">Every check working</Chip>
              : ins.faults_accepted_at
                ? <Chip tone="amber">{ins.fail_count} fault{ins.fail_count === 1 ? '' : 's'}, accepted</Chip>
                : <Chip tone="red">{ins.fail_count} fault{ins.fail_count === 1 ? '' : 's'}</Chip>}
          </div>
          <p className="text-muted-foreground">
            Odometer {ins.odometer_km.toLocaleString('en-ZA')} km, {ins.photo_count} photos
            {ins.earlier > 0 ? `, ${ins.earlier} earlier inspection${ins.earlier === 1 ? '' : 's'}` : ''}.
            {ins.notes ? ` ${ins.notes}` : ''}
          </p>
          {faults.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-foreground">
              {faults.map((f) => <li key={f.key}><span className="font-medium">{f.label}:</span> {f.note}{f.photo && <> <PhotoLink path={f.photo} /></>}</li>)}
            </ul>
          )}
          {ins.faults_accepted_at && (
            <p className="text-foreground">
              Accepted by {ins.faults_accepted_by_name ?? 'staff'} {formatWhen(ins.faults_accepted_at)}: {ins.faults_accepted_reason}
            </p>
          )}
          <button type="button" className="flex items-center gap-1 text-muted-foreground hover:text-foreground" onClick={() => setShowAll((v) => !v)}>
            {showAll ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Every answer and photo
          </button>
          {showAll && <AllAnswers answers={ins.answers} />}
          {canAct && !handedBack && ins.fail_count > 0 && !ins.faults_accepted_at && (
            <AcceptFaults inspectionId={ins.id} onChanged={onChanged} />
          )}
        </div>
      )}
      {canAct && !handedBack && (
        <Button size="sm" variant={ins ? 'outline' : 'default'} onClick={() => setFormOpen(true)}>
          <ClipboardCheck className="mr-1 h-3.5 w-3.5" />{ins ? 'Inspect again' : 'Start the inspection'}
        </Button>
      )}
      {formOpen && <InspectionForm c={c} onClose={() => setFormOpen(false)} onFiled={() => { setFormOpen(false); onChanged() }} />}
    </div>
  )
}

function AllAnswers({ answers }: { answers: OffboardInspection['answers'] }) {
  const sections: { label: string; rows: typeof answers }[] = []
  for (const a of answers) {
    const last = sections[sections.length - 1]
    if (last && last.label === a.section_label) last.rows.push(a)
    else sections.push({ label: a.section_label, rows: [a] })
  }
  return (
    <div className="space-y-2">
      {sections.map((s) => (
        <div key={s.label}>
          <p className="font-semibold text-foreground">{s.label}</p>
          <ul className="space-y-0.5">
            {s.rows.map((a) => (
              <li key={a.key} className="flex flex-wrap items-baseline gap-x-2">
                <span className={a.result === 'fail' ? 'text-rose-700 dark:text-rose-300' : 'text-foreground'}>{a.label}</span>
                <span className="text-muted-foreground">
                  {a.result === 'pass' ? 'working' : a.result === 'fail' ? `fault: ${a.note ?? ''}` : a.result === 'na' ? 'not applicable' : ''}
                </span>
                {a.photo && <PhotoLink path={a.photo} />}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

function PhotoLink({ path }: { path: string }) {
  const open = useOpenOwnerInspectionPhoto<{ url: string }>()
  async function go() {
    try {
      const r = await open.trigger({ path })
      if (r?.url) window.open(r.url, '_blank', 'noopener')
    } catch (e) {
      toast.error(message(e))
    }
  }
  return (
    <button type="button" className="inline-flex items-center gap-0.5 text-[hsl(var(--brand))] underline-offset-2 hover:underline" onClick={() => void go()}>
      photo <ExternalLink className="h-3 w-3" />
    </button>
  )
}

function AcceptFaults({ inspectionId, onChanged }: { inspectionId: string; onChanged: () => void }) {
  const accept = useAcceptOwnerInspectionFaults()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  async function go() {
    setError(null)
    try { await accept.trigger({ inspection_id: inspectionId, reason }); onChanged() } catch (e) { setError(message(e)) }
  }
  return (
    <div className="space-y-1 border-t border-border pt-2">
      <p className="text-muted-foreground">
        Fix the faults and press Inspect again. If the owner takes the car as it is, write why and who agreed; the hand back can then be recorded.
      </p>
      <div className="flex flex-wrap gap-1">
        <Input className="h-8 min-w-[14rem] flex-1 text-xs" placeholder="Owner will fix it, agreed with ..." value={reason} onChange={(e) => setReason(e.target.value)} />
        <Button size="sm" variant="outline" disabled={accept.loading} onClick={() => void go()}>
          {accept.loading && <Spinner className="mr-1 h-3.5 w-3.5" />}Owner takes it as it is
        </Button>
      </div>
      {error && <p className="text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function InspectionForm({ c, onClose, onFiled }: { c: OffboardCase; onClose: () => void; onFiled: () => void }) {
  const checklist = useGetOwnerInspectionChecklist<InspectionItem[]>()
  const record = useRecordOwnerOffboardingInspection<{ ok: boolean; fail_count: number }>()
  const [draft, setDraft] = useState<Draft>(() => readDraft(c.id))
  const [error, setError] = useState<string | null>(null)
  const [showMissing, setShowMissing] = useState(false)

  const load = checklist.trigger
  useEffect(() => { void load({}).catch(() => undefined) }, [load])
  useEffect(() => { saveDraft(c.id, draft) }, [c.id, draft])
  // An answer given clears the sentence about what was missing; Submit says it again if still true.
  useEffect(() => { setError(null) }, [draft])

  const items = useMemo(() => checklist.data ?? [], [checklist.data])
  const missing = useMemo(
    () => items.filter((i) => answerMissing(i, draft.answers[i.key]) !== null),
    [items, draft.answers],
  )
  const odometerOk = /^\d+$/.test(draft.odometer.trim()) && Number(draft.odometer) > 0

  const setAnswer = useCallback((key: string, patch: Partial<Answer>) => {
    setDraft((d) => ({ ...d, answers: { ...d.answers, [key]: { ...d.answers[key], ...patch } } }))
  }, [])

  const sections = useMemo(() => {
    const out: { key: string; label: string; items: InspectionItem[] }[] = []
    for (const i of items) {
      const last = out[out.length - 1]
      if (last && last.key === i.section) last.items.push(i)
      else out.push({ key: i.section, label: i.section_label, items: [i] })
    }
    return out
  }, [items])

  async function submit() {
    setError(null)
    setShowMissing(true)
    if (!odometerOk) { setError('Type the odometer reading, in kilometres, from the dashboard.'); return }
    if (missing.length > 0) {
      setError(`${missing.length} still to do. Each one is marked in red below.`)
      document.getElementById(`insp-${missing[0]!.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    try {
      const r = await record.trigger({
        id: c.id, request_key: draft.request_key, odometer_km: Number(draft.odometer), notes: draft.notes,
        answers: items.map((i) => ({ key: i.key, ...draft.answers[i.key] })),
      })
      clearDraft(c.id)
      toast.success(r?.fail_count ? `Inspection filed with ${r.fail_count} fault${r.fail_count === 1 ? '' : 's'}.` : 'Inspection filed. Every check working.')
      onFiled()
    } catch (e) {
      setError(message(e))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:h-[92vh] sm:max-h-[92vh]">
        <DialogHeader className="border-b border-border px-4 py-3 text-left">
          <DialogTitle>Offboarding inspection, {c.plate}</DialogTitle>
          <DialogDescription>
            Walk round the car and through every check. Photos upload as you take them and your answers stay on this phone until you submit.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
          {checklist.loading && items.length === 0 && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the checklist…</p>}
          {checklist.error && <p className="text-sm text-rose-700 dark:text-rose-300">{checklist.error}</p>}

          {items.length > 0 && (
            <section className="space-y-2">
              <label className="block text-sm">
                <span className="font-medium text-foreground">Odometer (km)</span>
                <Input inputMode="numeric" className={`mt-1 h-10 ${showMissing && !odometerOk ? 'border-rose-500' : ''}`} value={draft.odometer}
                  onChange={(e) => setDraft((d) => ({ ...d, odometer: e.target.value.replace(/[^\d]/g, '') }))} placeholder="From the dashboard" />
              </label>
            </section>
          )}

          {sections.map((s) => (
            <section key={s.key} className="space-y-2">
              <h3 className="sticky -top-3 z-10 -mx-4 border-b border-border bg-card px-4 py-1.5 text-sm font-semibold text-foreground">
                {s.label}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {s.items.filter((i) => answerMissing(i, draft.answers[i.key]) === null).length} of {s.items.length}
                </span>
              </h3>
              {s.items.map((i) => (
                <ItemRow key={i.key} caseId={c.id} item={i} answer={draft.answers[i.key]} flag={showMissing ? answerMissing(i, draft.answers[i.key]) : null}
                  onChange={(patch) => setAnswer(i.key, patch)} />
              ))}
            </section>
          ))}

          {items.length > 0 && (
            <label className="block text-sm">
              <span className="font-medium text-foreground">Anything else the owner should know</span>
              <Textarea className="mt-1 min-h-[4rem]" value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
            </label>
          )}
        </div>

        <div className="space-y-1 border-t border-border px-4 py-3">
          {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
          <div className="flex items-center gap-2">
            <span className="flex-1 text-xs text-muted-foreground">
              {items.length === 0 ? '' : missing.length === 0 ? 'Everything answered.' : `${items.length - missing.length} of ${items.length} done`}
            </span>
            <Button variant="outline" size="sm" onClick={onClose}>Close, keep the draft</Button>
            <Button size="sm" disabled={record.loading || items.length === 0} onClick={() => void submit()}>
              {record.loading && <Spinner className="mr-1 h-3.5 w-3.5" />}Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ItemRow({ caseId, item, answer, flag, onChange }: {
  caseId: string; item: InspectionItem; answer: Answer | undefined; flag: string | null; onChange: (patch: Partial<Answer>) => void
}) {
  const r = answer?.result
  const needsPhoto = item.kind === 'photo' || (item.photo === 'always' && r !== 'na') || (item.photo === 'on_fail' && r === 'fail')
  const offersPhoto = needsPhoto || (item.photo !== 'never' && r !== 'na')
  return (
    <div id={`insp-${item.key}`} className={`rounded-md border p-2.5 ${flag ? 'border-rose-400 bg-rose-50/50 dark:bg-rose-950/30' : 'border-border'}`}>
      <p className="text-sm font-medium text-foreground">{item.label}</p>
      {item.help && <p className="text-xs text-muted-foreground">{item.help}</p>}
      {item.kind === 'check' && (
        <div className="mt-2 flex flex-wrap gap-1">
          <Choice on={r === 'pass'} tone="green" onClick={() => onChange({ result: 'pass' })}>Working</Choice>
          <Choice on={r === 'fail'} tone="red" onClick={() => onChange({ result: 'fail' })}>Fault</Choice>
          {item.allows_na && <Choice on={r === 'na'} tone="neutral" onClick={() => onChange({ result: 'na' })}>Not applicable</Choice>}
        </div>
      )}
      {r === 'fail' && (
        <Input className="mt-2 h-9 text-sm" placeholder="What is wrong" value={answer?.note ?? ''} onChange={(e) => onChange({ note: e.target.value })} />
      )}
      {offersPhoto && <PhotoPicker caseId={caseId} item={item} path={answer?.photo} required={needsPhoto} onChange={(photo) => onChange({ photo })} />}
      {flag && <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">{flag}</p>}
    </div>
  )
}

function Choice({ on, tone, onClick, children }: { on: boolean; tone: 'green' | 'red' | 'neutral'; onClick: () => void; children: React.ReactNode }) {
  const active = tone === 'green' ? 'border-emerald-600 bg-emerald-600 text-white' : tone === 'red' ? 'border-rose-600 bg-rose-600 text-white' : 'border-foreground bg-foreground text-background'
  return (
    <button type="button" onClick={onClick}
      className={`h-9 min-w-[5.5rem] rounded-md border px-3 text-sm ${on ? active : 'border-border bg-background text-foreground hover:bg-accent'}`}>
      {children}
    </button>
  )
}

function PhotoPicker({ caseId, item, path, required, onChange }: {
  caseId: string; item: InspectionItem; path: string | undefined; required: boolean; onChange: (path: string | undefined) => void
}) {
  const sign = useSignOwnerInspectionPhoto<Signed>()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  async function take(file: File) {
    if (!(file.type || '').startsWith('image/')) { toast.error('Choose a photo.'); return }
    if (file.size > 15 * 1024 * 1024) { toast.error('That photo is over 15 MB. Take it again.'); return }
    setBusy(true)
    try {
      const signed = await sign.trigger({ id: caseId, filename: `${item.key}.${(file.name.split('.').pop() || 'jpg').toLowerCase()}` }, { skipCache: true })
      const saved = await uploadSigned(signed, file)
      setPreview(URL.createObjectURL(file))
      onChange(saved)
    } catch (e) {
      toast.error(`${item.label}: ${message(e)}`)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <input ref={input} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void take(f) }} />
      {preview && <img src={preview} alt="" className="h-12 w-12 rounded object-cover" />}
      <Button type="button" size="sm" variant={path ? 'outline' : required ? 'default' : 'ghost'} disabled={busy} onClick={() => input.current?.click()}>
        {busy ? <Spinner className="mr-1 h-3.5 w-3.5" /> : path ? <Check className="mr-1 h-3.5 w-3.5" /> : <Camera className="mr-1 h-3.5 w-3.5" />}
        {busy ? 'Uploading…' : path ? 'Photo saved, retake' : required ? 'Take the photo' : 'Add a photo'}
      </Button>
      {path && !required && (
        <button type="button" aria-label="Remove the photo" className="text-muted-foreground hover:text-foreground" onClick={() => { setPreview(null); onChange(undefined) }}>
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
